import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Bell, BookMarked, BookOpen, PlayCircle, Settings } from 'lucide-react';
import { VideoGuidaModal } from '../../components/support/guide/VideoGuidaPlayer';
import NovitaModal from '../../components/support/novita/NovitaModal';
import { useNovita } from '../../components/support/novita/useNovita';
import { useToast } from '../../contexts/ToastContext';
import AssociativeAppShell from '../../components/AssociativeAppShell';
import SupportQueue from '../../components/support/v3/SupportQueue';
import { getMyProfile, removeRepresentativeToken } from '../../services/representativeService';
import useSupportSocket from '../../hooks/useSupportSocket';
import representativeSupportApi from '../../services/adapters/representativeSupportApi';
// Token `--a2-*` (dichiarati su `.adm2, .sup3`) e classi `.s3-*`: il contenitore
// V3 porta la sola classe `.sup3`, mai `.adm2`, che imporrebbe sfondo di pagina
// e `min-height: 100vh` dentro la shell dell'area associativa.
import '../../styles/admin-v2.css';
import '../../styles/support-v3.css';
import { SupportLookProvider, supportRootClass } from '../../components/support/v4/lookContext';
import RepTitleBar, {
  REP_CHANNELS_LABEL,
  REP_CHANNELS_PATH,
  REP_CRUMBS,
  RepNotice,
  repTools,
  useCanManageChannels,
  type RepKpiNavState,
} from '../../components/support/v4/RepTitleBar';
import { buildKpis, type QueueQuickFilter, type S3Kpi } from '../../components/support/v3/kpiModel';
import { channelFilterLabel, channelFilterVisible, hasSupportQueue } from '../../components/support/v3/queueModel';
import type { GroupLite, StatsData } from '../../services/supportApi';
import ChannelAvatarMark from '../../components/support/ChannelAvatarMark';
import { hasChannelAvatar } from '../../components/support/channelAvatar';

interface RepProfile {
  _id: string;
  nome: string;
  cognome: string;
  tipo: 'interno' | 'esterno';
  caricaPrincipale?: string;
  foto?: string;
  associatesAccess?: boolean;
  permissions: { name: string; description: string }[];
}

// Carica senza `associativa.supporto.read`: il backend risponde 403. Non e una
// sessione scaduta, quindi niente rimando al login: si resta nella shell.
const FORBIDDEN_TEXT = {
  permesso: {
    title: 'Funzione non abilitata per la tua carica',
    body: 'La sezione Supporto non è attiva per il tuo ruolo associativo. Se pensi che ti serva, chiedi alla segreteria nazionale di abilitarla dai permessi associativi.',
  },
  canali: {
    title: 'Nessun canale di supporto assegnato',
    body: 'Hai il permesso per il Supporto ma non sei ancora componente di alcun canale: i ticket si vedono solo per i canali di cui si fa parte. Chiedi alla segreteria nazionale di aggiungerti a un canale.',
  },
} as const;

const SupportForbidden: React.FC<{ reason: 'permesso' | 'canali'; look: 'v3' | 'v4' }> = ({ reason, look }) => (
  look === 'v4' ? (
    <RepNotice title={FORBIDDEN_TEXT[reason].title}>{FORBIDDEN_TEXT[reason].body}</RepNotice>
  ) : (
  <div className="rounded-xl border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/20 p-5 max-w-xl">
    <p className="text-sm font-semibold text-amber-900 dark:text-amber-200">
      {reason === 'permesso' ? 'Funzione non abilitata per la tua carica' : 'Nessun canale di supporto assegnato'}
    </p>
    <p className="text-sm text-amber-900/80 dark:text-amber-200/80 mt-1">
      {reason === 'permesso'
        ? 'La sezione Supporto non è attiva per il tuo ruolo associativo. Se pensi che ti serva, chiedi alla segreteria nazionale di abilitarla dai permessi associativi.'
        : 'Hai il permesso per il Supporto ma non sei ancora componente di alcun canale: i ticket si vedono solo per i canali di cui si fa parte. Chiedi alla segreteria nazionale di aggiungerti a un canale.'}
    </p>
  </div>
  )
);

// Contenitore della coda ticket per il rappresentante: profilo, shell dell'area
// associativa e realtime. Tutta la UI della coda vive in `SupportQueue` (V3).
const AssociativeSupport: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const toast = useToast();
  const api = representativeSupportApi;

  const [profile, setProfile] = useState<RepProfile | null>(null);
  const [forbidden, setForbidden] = useState<null | 'permesso' | 'canali'>(null);
  const [videoOpen, setVideoOpen] = useState(false);
  const canLive = profile?.permissions?.some((p) => p.name === 'associativa.live.publish') ?? false;
  // Base di conoscenza (Task 29): link solo con il permesso di lettura.
  const canKnowledge = profile?.permissions?.some((p) => p.name === 'associativa.supporto.knowledge_read') ?? false;
  // Redesign v4 (T32): pelle opt-in col permesso di anteprima; finche il profilo
  // non e arrivato si resta sul v3. La radice `.sup3` e dentro SupportQueue:
  // `sup4` va sul contenitore `.page` (i token --s4-* si ereditano).
  const look = profile?.permissions?.some((p) => p.name === 'associativa.supporto.v4_preview') ? 'v4' : 'v3';
  const v4 = look === 'v4';

  // ===== Barra v4 (T41): i contatori salgono dalla coda alla barra (RA-04) con
  // TOTALI (N22). Li carica la pagina; la coda li spegne (`showStats={false}`).
  const [stats, setStats] = useState<StatsData | null>(null);
  const [myChannels, setMyChannels] = useState<{ label: string; group: GroupLite }[]>([]);
  const [queueFilter, setQueueFilter] = useState<{ status: string; unread: boolean; quick?: QueueQuickFilter }>({ status: '', unread: false });
  // Una pillola del dettaglio arriva come `location.state.kpiFilter`: applicata
  // una volta, al montaggio (nonce 1). `quick` (Task 46): pillola «BOZZE».
  const [externalFilter, setExternalFilter] = useState<{ status?: string; unread?: boolean; quick?: QueueQuickFilter; nonce: number } | undefined>(() => {
    const f = (location.state as RepKpiNavState | null)?.kpiFilter;
    return f ? { status: f.status, unread: f.unread, ...(f.quick ? { quick: f.quick } : {}), nonce: 1 } : undefined;
  });
  const loadStats = useCallback(() => {
    api.getStats()
      .then(setStats)
      .catch(() => { /* la barra resta senza pillole */ });
  }, [api]);
  const loadStatsRef = useRef<(() => void) | null>(null);
  loadStatsRef.current = v4 ? loadStats : null;
  useEffect(() => {
    if (v4) loadStats();
  }, [v4, loadStats]);
  const pickKpi = useCallback((k: S3Kpi) => {
    if (!k.filter) return;
    const f = k.filter;
    setExternalFilter((prev) => ({ status: f.status, unread: f.unread, quick: f.quick || '', nonce: (prev?.nonce ?? 0) + 1 }));
  }, []);
  const onQueueFilterChange = useCallback(
    (f: { status: string; unread: boolean; quick?: QueueQuickFilter }) =>
      setQueueFilter({ status: f.status, unread: f.unread, quick: f.quick || '' }),
    []
  );
  // Responsabile di canale (Task 46): ingranaggio verso le impostazioni dei canali.
  const canManageChannels = useCanManageChannels(
    api,
    profile?.permissions?.some((p) => p.name === 'associativa.supporto.manage_channel') ?? false
  );

  // Popup «Novità del Centro Ticket»: parte solo a profilo caricato (servono i permessi).
  const permessiNovita = useMemo(() => (profile ? (profile.permissions || []).map((p) => p.name) : null), [profile]);
  const novita = useNovita(api, permessiNovita, 'rep');

  // ===== Profilo (pattern comune area associativa) =====
  useEffect(() => {
    const token = localStorage.getItem('representative_token');
    if (!token) {
      navigate('/accesso-associati');
      return;
    }
    const loadProfile = async () => {
      try {
        const res = await getMyProfile();
        if (res.success) setProfile(res.data);
      } catch (err: any) {
        if (err.response?.status === 401 || err.response?.status === 403) {
          removeRepresentativeToken();
          navigate('/accesso-associati');
          return;
        }
        toast.error('Errore caricamento profilo');
      }
    };
    loadProfile();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate]);

  // Senza canali di cui si e componente la coda sarebbe vuota per costruzione:
  // meglio dirlo. Non se c'e un ticket assegnato come esterno al canale
  // (`assignedCount`, Task 26) o un invito in un ticket (`invitedCount`, parte B):
  // la coda lo mostra. Il 403 (permesso mancante)
  // lo intercetta gia la coda.
  useEffect(() => {
    let cancelled = false;
    api.getGroups()
      .then((res) => {
        if (!cancelled && !hasSupportQueue(res)) setForbidden('canali');
        if (!cancelled) {
          setMyChannels((res.mine || []).filter((g) => channelFilterVisible(g)).map((g) => ({ label: channelFilterLabel(g), group: g })));
        }
      })
      .catch(() => { /* gestito dalla coda (403) o dal toast della coda */ });
    return () => { cancelled = true; };
  }, [api]);

  // ===== Realtime coda: refetch lista (throttle ~1s) su eventi del gruppo =====
  const refetchRef = useRef<(() => void) | null>(null);
  const lastRefetchRef = useRef<number>(0);
  const refetchTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const throttledRefetch = useCallback(() => {
    const now = Date.now();
    const elapsed = now - lastRefetchRef.current;
    if (elapsed >= 1000) {
      lastRefetchRef.current = now;
      refetchRef.current?.();
      loadStatsRef.current?.();
    } else if (!refetchTimerRef.current) {
      refetchTimerRef.current = setTimeout(() => {
        refetchTimerRef.current = null;
        lastRefetchRef.current = Date.now();
        refetchRef.current?.();
        loadStatsRef.current?.();
      }, 1000 - elapsed);
    }
  }, []);

  useEffect(() => () => {
    if (refetchTimerRef.current) clearTimeout(refetchTimerRef.current);
  }, []);

  useSupportSocket({
    area: 'representative',
    queue: true,
    onGroupEvent: () => throttledRefetch(),
  });

  return (
    <SupportLookProvider look={look}>
    <AssociativeAppShell
      route="supporto"
      nome={profile?.nome}
      cognome={profile?.cognome}
      caricaPrincipale={profile?.caricaPrincipale}
      tipo={profile?.tipo}
      foto={profile?.foto}
      associatesAccess={profile?.associatesAccess}
      canLive={canLive}
    >
      <div className={supportRootClass('page fade-in', look)}>
        {v4 && !forbidden && (
          <RepTitleBar
            crumbs={REP_CRUMBS}
            title="Ticket di supporto"
            kpis={buildKpis(stats).filter((k) => k.key !== 'dispatch')}
            current={queueFilter}
            onPickKpi={pickKpi}
            tools={[
              { label: 'Video guida', onClick: () => setVideoOpen(true), icon: <PlayCircle size={14} aria-hidden="true" /> },
              ...repTools(canKnowledge, canManageChannels),
            ]}
          />
        )}
        {v4 && !forbidden && myChannels.length > 0 && (
          <p className="sup3 s4-repchannels">
            {/* Senza avatar il testo resta quello di sempre; con avatar ogni nome ha la sua emoji/immagine. */}
            {myChannels.some((c) => hasChannelAvatar(c.group)) ? (
              <>
                I tuoi canali ·{' '}
                {myChannels.map((c, i) => (
                  <React.Fragment key={c.group._id}>
                    {i > 0 && ', '}
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, verticalAlign: 'middle' }}>
                      <ChannelAvatarMark channel={c.group} size={14} />
                      {c.label}
                    </span>
                  </React.Fragment>
                ))}
              </>
            ) : (
              `I tuoi canali · ${myChannels.map((c) => c.label).join(', ')}`
            )}
          </p>
        )}
        {forbidden ? <SupportForbidden reason={forbidden} look={look} /> : (
        <SupportQueue
          api={api}
          layout="table"
          selfId={profile?._id}
          {...(v4 ? {
            showHeader: false,
            showStats: false,
            externalFilter,
            onFilterChange: onQueueFilterChange,
          } : {})}
          headerActions={
            <span className="inline-flex items-center gap-3">
              <button
                type="button"
                onClick={() => setVideoOpen(true)}
                className="s3-link text-xs inline-flex items-center gap-1"
              >
                <PlayCircle size={12} aria-hidden="true" /> Video guida
              </button>
              <Link to="/area-associativa/guida-ticket" className="s3-link text-xs inline-flex items-center gap-1">
                <BookOpen size={12} aria-hidden="true" /> Guida all&apos;uso
              </Link>
              <Link to="/area-associativa/supporto/notifiche" className="s3-link text-xs inline-flex items-center gap-1">
                <Bell size={12} aria-hidden="true" /> Notifiche email
              </Link>
              {canKnowledge && (
                <Link to="/area-associativa/supporto/conoscenza" className="s3-link text-xs inline-flex items-center gap-1">
                  <BookMarked size={12} aria-hidden="true" /> Base di conoscenza
                </Link>
              )}
              {canManageChannels && (
                <Link to={REP_CHANNELS_PATH} className="s3-link text-xs inline-flex items-center gap-1">
                  <Settings size={12} aria-hidden="true" /> {REP_CHANNELS_LABEL}
                </Link>
              )}
            </span>
          }
          onOpenTicket={(id) => navigate(api.ticketPath(id))}
          onAuthError={(status) => { if (status === 403) setForbidden('permesso'); else navigate('/accesso-associati'); }}
          onReady={(refetch) => {
            refetchRef.current = refetch;
            // La coda puo smontarsi prima della pagina: un refetch orfano
            // scriverebbe stato su un albero morto.
            return () => {
              if (refetchRef.current === refetch) refetchRef.current = null;
            };
          }}
        />
        )}
      </div>
      <VideoGuidaModal open={videoOpen} onClose={() => setVideoOpen(false)} />
      <NovitaModal open={novita.open && !forbidden} voci={novita.voci} area="rep" onClose={novita.chiudi} />
    </AssociativeAppShell>
    </SupportLookProvider>
  );
};

export default AssociativeSupport;
